/**
 * HISTORIE ZMEN LIDSKY (krok UXF4 bod 7, nalezy #40 a N11).
 *
 * Audit GPT 9. a 10. 10. 2026: obrazovka „Historie změn" ukazovala rovnou
 * strojovy vypis — `party.created`, `01a05edd-…`, otisk retezu, „Měsíční
 * kotvy (WORM)" a Export JSON. Makler z toho nepozna, co se v kancelari
 * stalo.
 *
 * Tenhle modul je CISTY VYPOCET nad `audit_events` — zadny novy endpoint,
 * zadny druhy zdroj pravdy. Z udalosti vyrobi jednu vetu v nominativu:
 *
 *   „Změna stavu zakázky"   (`case.stage_changed`, entityType `case`)
 *   „Nahrání dokumentu"     (`document.upload`,    entityType `document`)
 *
 * Veta se sklada ze DVOU casti, aby nerostla s 368 akcemi, ktere repo zapisuje
 * (`rg "action: '"`): SLOVESNE PODSTATNE JMENO z posledniho segmentu akce
 * (nominativ) a PREDMET z `entityType` (genitiv). Nova akce `*.created` nad
 * novou tabulkou tak dostane vetu i bez zasahu sem; kdyz neznáme ani jedno,
 * vyjde „Změna záznamu" — pravda, ne vymysl.
 *
 * ROZHODNUTI UXF4-AUDIT-KDO: u uzivatele se ukazuje ROLE z katalogu, ne
 * `actorId`. Jmeno uzivatele udalost nenese a dohledavat ho znamena druhy
 * dotaz na kazdy radek; UUID na obrazovce makleere je presne to, co audit
 * vytykal. Seznam roli je typovany na `StaffRole` z `@reos/policy` — pridani
 * role shodi typecheck, dokud nema popisek.
 */

import { t, type MessageKey } from '@reos/i18n';
import { isStaffRole, type StaffRole } from '@reos/policy';
import { brokerText } from './broker-text.js';

const cs = { locale: 'cs' } as const;

/** Co z udalosti potrebuje lidska veta — podmnozina `AuditEvent`. */
export interface AuditEventLike {
  readonly occurredAt: string;
  readonly actorType: string;
  readonly actorRole: string | null;
  readonly action: string;
  readonly entityType: string | null;
  readonly reason: string | null;
}

/**
 * Posledni segment akce -> klic slovesneho podstatneho jmena. Synonyma
 * (`create`/`created`, `removed`/`revoked`) mluvi stejne, proto jeden klic.
 */
const VERB_KEYS: Readonly<Record<string, string>> = {
  created: 'created',
  create: 'created',
  updated: 'updated',
  update: 'updated',
  set: 'updated',
  rescored: 'updated',
  deleted: 'deleted',
  removed: 'deleted',
  revoked: 'deleted',
  archived: 'deleted',
  redacted: 'deleted',
  sent: 'sent',
  replied: 'sent',
  reminded: 'sent',
  submitted: 'submitted',
  uploaded: 'uploaded',
  upload: 'uploaded',
  signed: 'signed',
  sign: 'signed',
  paid: 'paid',
  mark_paid: 'paid',
  refunded: 'refunded',
  issued: 'issued',
  approved: 'approved',
  decide: 'approved',
  confirmed: 'confirmed',
  rejected: 'rejected',
  resolved: 'resolved',
  stage_changed: 'statusChanged',
  status_changed: 'statusChanged',
  queued: 'queued',
  requeued: 'queued',
  done: 'done',
  completed: 'done',
  failed: 'failed',
  cancelled: 'cancelled',
  merged: 'merged',
  added: 'added',
  read: 'read',
  recorded: 'recorded',
  rendered: 'rendered',
  opened: 'opened',
};

/**
 * `entityType` -> klic predmetu v GENITIVU („zakázky", „smlouvy"). Prefix
 * staci: `contract_clause` i `contract_signature_session` je pro makleere
 * porad smlouva, `finance_rent_payment` platba.
 */
export const NOUN_PREFIX_KEYS: readonly (readonly [string, string])[] = [
  ['acquisition_lead', 'lead'],
  // Krok QAFIX11 bod 2 (nalez V8): `ai_credit_topup_intents` padalo do
  // „záznamu", takze dobiti kreditu nebylo z vety poznat a znacila ho jen
  // surova poznamka pod ni.
  ['ai_credit', 'aiCredit'],
  ['audit_case', 'case'],
  ['dd_case', 'case'],
  ['case', 'case'],
  ['contract', 'contract'],
  ['party', 'client'],
  ['client_intake', 'clientIntake'],
  ['document', 'document'],
  ['file', 'document'],
  ['property', 'property'],
  ['commerce_order', 'order'],
  ['crm_task', 'task'],
  ['crm_calendar', 'calendarEvent'],
  ['crm_demand', 'demand'],
  ['crm_viewing', 'viewing'],
  ['crm_public_listing', 'listing'],
  ['listing', 'listing'],
  ['publication', 'listing'],
  ['dd_report', 'report'],
  ['owner_report', 'report'],
  ['dd_export', 'export'],
  ['portal_export', 'export'],
  ['mcp_approval', 'approval'],
  ['invoice', 'invoice'],
  ['finance_client_invoice', 'invoice'],
  ['advertising_invoice', 'invoice'],
  ['payment', 'payment'],
  ['finance_rent', 'rent'],
  ['pm_lease', 'lease'],
  ['user', 'user'],
  ['tenant', 'office'],
  ['communication', 'message'],
  ['client_mail', 'message'],
  ['broker_mail', 'message'],
];

function verbKey(action: string): string {
  const last = action.slice(action.lastIndexOf('.') + 1);
  const exact = VERB_KEYS[last];
  if (exact !== undefined) return exact;
  /*
   * Krok QAFIX11 bod 2 (nalez V8): SLOZENY POSLEDNI SEGMENT. Akce jako
   * `ai.credits.topup_intent_created` nebo `crm.public_listing_office_site_changed`
   * nesou sloveso az na konci segmentu, takze presna shoda selze a veta
   * vyjde „Změna záznamu" — makléř pak pozna udalost jen z poznamky pod ni,
   * tedy presne z toho surového textu, ktery nalez V8 vytykal. Druha
   * zastavka je proto SLOVO ZA POSLEDNIM PODTRZITKEM — obecne pravidlo pro
   * vsechny takove akce, ne vycet dvou.
   */
  return VERB_KEYS[last.slice(last.lastIndexOf('_') + 1)] ?? 'other';
}

function nounKey(entityType: string | null): string {
  if (entityType === null) return 'record';
  // PORADI V TABULCE ROZHODUJE — plati prvni sedici prefix. Ze mezi nimi neni
  // kratsi nad delsim (`commerce` nad `commerce_order`) hlida invariant
  // v `audit-human.spec.ts`; tim se to nemuze tise rozejit.
  const hit = NOUN_PREFIX_KEYS.find(([prefix]) => entityType.startsWith(prefix));
  return hit?.[1] ?? 'record';
}

/** Lidska veta o udalosti: „Změna stavu zakázky". */
export function auditEventSentence(event: AuditEventLike): string {
  const verb = t(`webAdmin.auditLog.human.verb.${verbKey(event.action)}` as MessageKey, cs);
  const noun = t(`webAdmin.auditLog.human.noun.${nounKey(event.entityType)}` as MessageKey, cs);
  return `${verb} ${noun}`;
}

/** Popisek role pro vsechny staff role — chybejici popisek shodi typecheck. */
const ROLE_LABEL_KEYS: Readonly<Record<StaffRole, string>> = {
  owner: 'owner',
  admin: 'admin',
  broker: 'broker',
  assistant: 'assistant',
  finance: 'finance',
  legal: 'legal',
  pm_manager: 'pmManager',
  viewer: 'viewer',
};

/** Kdo zmenu udelal — role z katalogu, nebo „automatická změna". */
export function auditActorText(event: AuditEventLike): string {
  if (event.actorType !== 'user') {
    return t('webAdmin.auditLog.human.actorAutomatic', cs);
  }
  const role = event.actorRole;
  if (role !== null && isStaffRole(role)) {
    return t(`webAdmin.auditLog.human.role.${ROLE_LABEL_KEYS[role]}` as MessageKey, cs);
  }
  return t('webAdmin.auditLog.human.actorUser', cs);
}

/**
 * ---------------------------------------------------------------------------
 * Krok QAFIX11 bod 2 (nalez V8 QA 21. a 22. 10.) — PODMINKA PRO POZNAMKU
 * V LIDSKEM VYPISU.
 *
 * QA nasla na `/audit` mezi ceskymi vetami dva surove zapisy: „kancelar
 * zahajila dobiti AI kreditu (krok KR1)" (zapisuje `packages/ai/src/topup.ts`)
 * a „zive overeni KR1 - vynulovani…" (zapisuje overovaci skript). Obe prosly
 * `brokerText`, protoze ten pozna chybejici diakritiku jen u SLOV ze slovniku
 * `MISSING_DIACRITICS_RULES` — a „kancelar", „zahajila" ani „vynulovani"
 * v nem nejsou.
 *
 * `audit_events.reason` je ale z devadesati procent pole PRO STROJ: pise ho
 * kod (`transitionCase`, `enableOfficeSite`, „… (krok KR1)"), ne clovek.
 * Lidsky vypis proto poznamku ukaze jen tehdy, kdyz PROJDE, a dve obecna
 * pravidla rikaji, co neprojde:
 *
 *   1. ZNACKA KROKU/BEHU — token velkych pismen s cislici (`KR1`, `WEBP2`,
 *      `QAFIX11`). To je stopa vyvoje, ne veta pro makléře; plati na vsech
 *      ~400 akcich repa, ne na dvou vyjimkach.
 *   2. CESKY TEXT BEZ DIAKRITIKY — tri a vic slov (3+ pismena) a ani jeden
 *      hacek nebo carka. Veta, kterou nekdo psal jako identifikator.
 *
 * Co se neukaze, NEMIZI — zustava v „Pro správce" (technicky vypis), presne
 * jak popisek karty slibuje. Pravidla stoji tady a ne v `brokerText`, protoze
 * `brokerText` hlida PERSISTOVANE TEXTY PRO KLIENTA (popisy, vety reportu),
 * kde „tri slova bez hacku" muze byt legitimni nazev; zdejsi pole je
 * provozni poznamka a cena za zamlceni je nulova.
 * ---------------------------------------------------------------------------
 */
const STEP_CODE_PATTERN = /\b[A-Z]{2,}[0-9]+\b/u;
const CZECH_DIACRITICS_PATTERN = /[áčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/u;
const WORD_PATTERN = /\p{L}{3,}/gu;

/** Je poznamka z auditu veta pro cloveka, nebo stopa stroje? */
export function isMachineReason(raw: string): boolean {
  if (STEP_CODE_PATTERN.test(raw)) return true;
  const words = raw.match(WORD_PATTERN) ?? [];
  return words.length >= 3 && !CZECH_DIACRITICS_PATTERN.test(raw);
}

/**
 * Je to zmena, kterou udelal clovek? Vychozi pohled „Historie změn" ukazuje
 * jen tyhle (zadani UXF4 bod 7); fronty, joby a konektory patri do rozbaleni
 * „Pro správce", kde je vypis kompletni.
 */
export function isUserChange(event: AuditEventLike): boolean {
  return event.actorType === 'user';
}

export interface AuditHumanRow {
  readonly sentence: string;
  readonly actor: string;
  readonly when: string;
  /** Duvod zmeny, kdyz je to veta pro cloveka (`brokerText`), jinak null. */
  readonly reason: string | null;
}

/**
 * Radek lidskeho pohledu. `reason` jde pres `brokerText` — v auditu jsou
 * i duvody typu `reason_code: tenant_switch`, a to je strojovy text (UX1).
 */
export function auditHumanRow(event: AuditEventLike): AuditHumanRow {
  const raw = event.reason === null || event.reason.trim() === '' ? null : event.reason;
  // Krok QAFIX11 bod 2 (nalez V8): nejdriv obecna pravidla pro poznamku
  // auditu, pak tentyz filtr strojoveho textu, jaky plati jinde v UI.
  const reason = raw === null || isMachineReason(raw) ? null : brokerText(raw, { fallback: '' });
  return {
    sentence: auditEventSentence(event),
    actor: auditActorText(event),
    when: new Date(event.occurredAt).toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    reason: reason === null || reason === '' ? null : reason,
  };
}
